import { FileInput, RadioTower } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  useFeedActions,
  useLabActions,
  useLabWorklist,
  useResultsInbox,
} from '@/features/orders/api';
import {
  RESULT_FLAG_LABEL,
  REVIEW_LABEL,
  type FeedMessage,
  type InboxResult,
  type LabResultFlag,
} from '@/features/orders/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB') : '—';

const flagBadge = (f: LabResultFlag | null) =>
  f && f !== 'normal' ? (
    <Badge tone={f === 'critical' ? 'danger' : 'warning'}>
      {RESULT_FLAG_LABEL[f]}
    </Badge>
  ) : (
    <Badge>Normal</Badge>
  );

/**
 * Admin v2 Lab "Results inbox" (artifact #116): the reference-lab feed (HL7
 * v2.5 ORU^R01, simulated until a lab is connected), messages that matched
 * nobody held for manual matching, every result with its flag and the doctor
 * it is routed to, and "Import result" for paper results. The lab never
 * releases a result; the review column shows what the doctor has done.
 */
export function ResultsInboxPage() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const can = hasPermission('lab_orders:process');
  const { data, isLoading, error, refetch } = useResultsInbox();
  const feed = useFeedActions();
  const toast = useToast();
  const [matching, setMatching] = useState<FeedMessage | null>(null);
  const [rejecting, setRejecting] = useState<FeedMessage | null>(null);
  const [importing, setImporting] = useState(false);
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });

  if (!branch)
    return (
      <PageContainer>
        <p className={styles.warnNote}>
          Choose the branch you are working at in the branch switch.
        </p>
      </PageContainer>
    );

  const messageColumns: Column<FeedMessage>[] = [
    {
      key: 'at',
      header: 'Received',
      render: (m) => (
        <>
          <p>{when(m.receivedAt)}</p>
          <p className={styles.sub + ' mono'}>{m.controlId}</p>
        </>
      ),
    },
    {
      key: 'patient',
      header: 'Patient on message',
      render: (m) => (
        <>
          <p>{m.patientName ?? '—'}</p>
          <p className={styles.sub}>
            IC …{m.identityLast4 ?? '—'} · order {m.orderNumber ?? '—'}
          </p>
        </>
      ),
    },
    {
      key: 'results',
      header: 'Results',
      render: (m) =>
        m.observations.map((o) => `${o.test} ${o.value}`).join(', '),
    },
    {
      key: 'status',
      header: 'Status',
      render: (m) =>
        m.status === 'imported' ? (
          <Badge tone="success">{`Matched · ${m.labOrder?.number ?? ''}`}</Badge>
        ) : m.status === 'unmatched' ? (
          <Badge tone="warning">Matched nobody · held</Badge>
        ) : (
          <Badge>Rejected</Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (m) =>
        can && m.status === 'unmatched' ? (
          <div className={styles.badges}>
            <Button size="sm" onClick={() => setMatching(m)}>
              Match
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRejecting(m)}>
              Reject
            </Button>
          </div>
        ) : null,
    },
  ];

  const resultColumns: Column<InboxResult>[] = [
    {
      key: 'order',
      header: 'Order',
      render: (r) => <span className="mono">{r.orderNumber}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (r) => <p className={styles.name}>{r.patient.fullName}</p>,
    },
    { key: 'test', header: 'Test', render: (r) => r.testName },
    {
      key: 'value',
      header: 'Value',
      render: (r) => (
        <>
          <strong>
            {r.value}
            {r.unit ? ` ${r.unit}` : ''}
          </strong>
          {r.referenceRange && (
            <span className={styles.sub}> (range {r.referenceRange})</span>
          )}
        </>
      ),
    },
    { key: 'flag', header: 'Flag', render: (r) => flagBadge(r.flag) },
    {
      key: 'routed',
      header: 'Routed to',
      render: (r) => (
        <>
          <p>{r.routedTo.fullName}</p>
          {r.review && (
            <Badge
              tone={
                r.review === 'released'
                  ? 'success'
                  : r.review === 'held'
                    ? 'danger'
                    : 'info'
              }
            >
              {REVIEW_LABEL[r.review]}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: 'source',
      header: 'From',
      render: (r) =>
        r.source === 'feed' ? 'Feed' : r.source === 'paper' ? 'Paper' : 'Typed',
    },
  ];

  const f = data?.feed;
  return (
    <PageContainer>
      <Card
        title="Reference-lab results feed"
        actions={
          f && (
            <Badge tone={f.connection === 'connected' ? 'success' : 'info'}>
              {`${f.protocol} · ${f.connection === 'connected' ? 'connected' : 'simulated'}`}
            </Badge>
          )
        }
      >
        <div className={styles.drawer}>
          <p className={styles.sub}>
            {f
              ? `${f.lab} · last message ${when(f.lastMessageAt)} · ${f.unmatched} held for manual matching`
              : '—'}
          </p>
          <p className={styles.sub}>
            Results are matched to the patient by IC and order number and go to
            the ordering doctor. No reference lab is connected yet; the demo
            message stands in for it.
          </p>
          {can && (
            <div className={styles.badges}>
              <Button
                icon={<RadioTower size={16} />}
                loading={feed.demo.isPending}
                onClick={() =>
                  feed.demo
                    .mutateAsync(false)
                    .then((m) =>
                      toast.show({
                        tone: m.status === 'imported' ? 'success' : 'info',
                        title:
                          m.status === 'imported'
                            ? `Results in for ${m.labOrder?.number ?? ''}`
                            : 'Message matched nobody: held for matching',
                      }),
                    )
                    .catch(fail)
                }
              >
                Receive next message (demo)
              </Button>
              <Button
                variant="ghost"
                onClick={() => void feed.demo.mutateAsync(true).catch(fail)}
              >
                Demo: message for an unknown patient
              </Button>
              <Button
                icon={<FileInput size={16} />}
                onClick={() => setImporting(true)}
              >
                Import result
              </Button>
            </div>
          )}
        </div>
      </Card>
      <Card title="Feed messages" flush>
        <DataTable
          caption="Feed messages"
          columns={messageColumns}
          rows={data?.messages}
          rowKey={(m) => m.id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={<EmptyState title="No messages received yet" />}
        />
      </Card>
      <Card title={`Results in · ${branch.name}`} flush>
        <DataTable
          caption="Results inbox"
          columns={resultColumns}
          rows={data?.results}
          rowKey={(r) => r.itemId}
          loading={isLoading}
          empty={
            <EmptyState
              title="No results yet"
              body="Results from the feed, paper reports or typed at the desk appear here."
            />
          }
        />
      </Card>
      {matching && (
        <MatchDialog
          message={matching}
          onClose={() => setMatching(null)}
          onError={fail}
        />
      )}
      {rejecting && (
        <RejectDialog
          message={rejecting}
          onClose={() => setRejecting(null)}
          onError={fail}
        />
      )}
      {importing && (
        <PaperDialog
          onClose={() => setImporting(false)}
          onError={fail}
          orderedByFallback={t('common.notRecorded')}
        />
      )}
    </PageContainer>
  );
}

/** RL-1844: why a held message is rejected (stored as the reason). */
const REJECT_REASONS = [
  'Not our patient',
  'Sample unusable',
  'Wrong test',
  'Duplicate',
  'Other',
] as const;

function RejectDialog({
  message: m,
  onClose,
  onError,
}: {
  message: FeedMessage;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const feed = useFeedActions();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const other = reason === 'Other';
  // The API keeps 3–300 characters.
  const sent = other ? `Other: ${note.trim()}` : reason;
  const ready = other ? note.trim().length >= 3 : reason !== '';
  return (
    <Dialog
      open
      variant="modal"
      title="Reject a held result"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={!ready}
            loading={feed.reject.isPending}
            onClick={() =>
              feed.reject
                .mutateAsync({ id: m.id, reason: sent })
                .then(onClose)
                .catch(onError)
            }
          >
            Reject
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p>
          {m.patientName ?? '—'} · IC …{m.identityLast4 ?? '—'} · order on the
          message {m.orderNumber ?? '—'}. The message is kept on record.
        </p>
        <Field label="Reason">
          <Select
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            options={[
              { value: '', label: 'Choose…' },
              ...REJECT_REASONS.map((r) => ({ value: r, label: r })),
            ]}
          />
        </Field>
        {other && (
          <Field label="Note" hint="At least 3 characters.">
            <Input
              value={note}
              maxLength={290}
              onChange={(ev) => setNote(ev.target.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}

function MatchDialog({
  message: m,
  onClose,
  onError,
}: {
  message: FeedMessage;
  onClose: () => void;
  onError: (err: unknown) => void;
}) {
  const { data } = useLabWorklist('open');
  const feed = useFeedActions();
  const [orderId, setOrderId] = useState('');
  const atLab = (data ?? []).filter(
    (o) => o.status === 'collected' || o.status === 'dispatched',
  );
  return (
    <Dialog
      open
      variant="modal"
      title="Match a held result"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!orderId}
            loading={feed.match.isPending}
            onClick={() =>
              feed.match
                .mutateAsync({ id: m.id, labOrderId: orderId })
                .then(onClose)
                .catch(onError)
            }
          >
            Match to this order
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <p>
          {m.patientName ?? '—'} · IC …{m.identityLast4 ?? '—'} · order on the
          message {m.orderNumber ?? '—'}
        </p>
        <Field
          label="Order at the lab"
          hint="The IC on the message must be this patient's."
        >
          <Select
            value={orderId}
            onChange={(ev) => setOrderId(ev.target.value)}
            options={[
              { value: '', label: 'Choose…' },
              ...atLab.map((o) => ({
                value: o.id,
                label: `${o.number ?? ''} · ${o.patient.fullName} · ${o.tests.map((t) => t.testName).join(', ')}`,
              })),
            ]}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** "Import result": a paper report from the reference lab, typed in. */
function PaperDialog({
  onClose,
  onError,
  orderedByFallback,
}: {
  onClose: () => void;
  onError: (err: unknown) => void;
  orderedByFallback: string;
}) {
  const { data } = useLabWorklist('open');
  const a = useLabActions();
  const toast = useToast();
  const [orderId, setOrderId] = useState('');
  const [values, setValues] = useState<
    Record<string, { value: string; unit: string; range: string; flag: string }>
  >({});
  const atLab = (data ?? []).filter(
    (o) => o.status === 'collected' || o.status === 'dispatched',
  );
  const order = atLab.find((o) => o.id === orderId);
  const filled = order
    ? order.tests.filter((t) => values[t.id]?.value.trim())
    : [];
  const set = (id: string, k: 'value' | 'unit' | 'range' | 'flag', v: string) =>
    setValues({
      ...values,
      [id]: {
        ...{ value: '', unit: '', range: '', flag: '' },
        ...values[id],
        [k]: v,
      },
    });
  return (
    <Dialog
      open
      wide
      title="Import result (paper)"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!order || filled.length === 0}
            loading={a.paperResults.isPending}
            onClick={() =>
              order &&
              a.paperResults
                .mutateAsync({
                  id: order.id,
                  markReady: filled.length === order.tests.length,
                  results: filled.map((t) => ({
                    itemId: t.id,
                    value: values[t.id].value.trim(),
                    unit: values[t.id].unit.trim() || undefined,
                    referenceRange: values[t.id].range.trim() || undefined,
                    flag: (values[t.id].flag || undefined) as
                      LabResultFlag | undefined,
                  })),
                })
                .then((o) => {
                  toast.show({
                    tone: 'success',
                    title:
                      o.status === 'results_ready'
                        ? `Results sent to ${o.orderedBy?.fullName ?? orderedByFallback}`
                        : 'Results saved',
                  });
                  onClose();
                })
                .catch(onError)
            }
          >
            Save results
          </Button>
        </>
      }
    >
      <div className={styles.drawer}>
        <Field label="Order at the lab">
          <Select
            value={orderId}
            onChange={(ev) => setOrderId(ev.target.value)}
            options={[
              { value: '', label: 'Choose…' },
              ...atLab.map((o) => ({
                value: o.id,
                label: `${o.number ?? ''} · ${o.patient.fullName}`,
              })),
            ]}
          />
        </Field>
        {order?.tests.map((t) => (
          <div key={t.id} className={styles.vitalsGrid}>
            <Field label={t.testName}>
              <Input
                aria-label={`Result for ${t.testName}`}
                value={values[t.id]?.value ?? ''}
                onChange={(ev) => set(t.id, 'value', ev.target.value)}
              />
            </Field>
            <Field label="Unit">
              <Input
                value={values[t.id]?.unit ?? ''}
                onChange={(ev) => set(t.id, 'unit', ev.target.value)}
              />
            </Field>
            <Field label="Range">
              <Input
                value={values[t.id]?.range ?? ''}
                onChange={(ev) => set(t.id, 'range', ev.target.value)}
              />
            </Field>
            <Field label="Flag">
              <Select
                value={values[t.id]?.flag ?? ''}
                onChange={(ev) => set(t.id, 'flag', ev.target.value)}
                options={[
                  { value: '', label: 'No flag' },
                  ...(Object.keys(RESULT_FLAG_LABEL) as LabResultFlag[]).map(
                    (fl) => ({ value: fl, label: RESULT_FLAG_LABEL[fl] }),
                  ),
                ]}
              />
            </Field>
          </div>
        ))}
        {order && (
          <p className={styles.sub}>
            With every test filled in, the results go to{' '}
            {order.orderedBy?.fullName ?? orderedByFallback} for review.
          </p>
        )}
      </div>
    </Dialog>
  );
}
